import React, { useState, useCallback, useRef } from 'react';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import './resizable-field.css';
import type { CertificateFieldMapping } from '@/types/certificate.types';
import { Edit2, X, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ResizableFieldProps {
  mapping: CertificateFieldMapping;
  screenX: number;
  screenY: number;
  screenWidth: number;
  screenHeight: number;
  pageDimensions: { width: number; height: number };
  pdfLibDimensions: { width: number; height: number } | null;
  pixelToPointRatio: number | null;
  pageRect: DOMRect;
  onUpdate: (updates: Partial<CertificateFieldMapping>) => void;
  onDelete: () => void;
  onEdit: () => void;
  getFieldTypeLabel: (type: string) => string;
  getPlaceholderText: (mapping: CertificateFieldMapping) => string;
  isSelected?: boolean;
  onSelect?: () => void;
}

export const ResizableField: React.FC<ResizableFieldProps> = ({
  mapping,
  screenX,
  screenY,
  screenWidth,
  screenHeight,
  pageDimensions,
  pdfLibDimensions,
  pixelToPointRatio,
  pageRect,
  onUpdate,
  onDelete,
  onEdit,
  getFieldTypeLabel,
  getPlaceholderText,
  isSelected = false,
  onSelect,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [currentWidth, setCurrentWidth] = useState(screenWidth);
  const [currentHeight, setCurrentHeight] = useState(screenHeight);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdateRef = useRef<Partial<CertificateFieldMapping> | null>(null);

  const isQRCode = mapping.fieldType === 'QR_CODE';
  const isPhoto = mapping.fieldType === 'USER_PROFILE' && mapping.userProfileField === 'photo';
  // QR codes: 1:1 (square), Photos: 7:9 (portrait, ICAO passport photo standard)
  const aspectRatio = isQRCode ? 1 : isPhoto ? 7/9 : undefined;

  // Update local dimensions when props change
  React.useEffect(() => {
    setCurrentWidth(screenWidth);
    setCurrentHeight(screenHeight);
  }, [screenWidth, screenHeight]);

  // Debounced save function
  const debouncedSave = useCallback((updates: Partial<CertificateFieldMapping>) => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Store the pending update
    pendingUpdateRef.current = updates;

    // Set a new timer to save after 500ms of no resize activity
    debounceTimerRef.current = setTimeout(() => {
      if (pendingUpdateRef.current) {
        onUpdate(pendingUpdateRef.current);
        pendingUpdateRef.current = null;
      }
    }, 500);
  }, [onUpdate]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (
      e.target instanceof HTMLElement && 
      (e.target.closest('.resize-handle') || 
       e.target.closest('.react-resizable-handle') ||
       isResizing)
    ) {
      return;
    }
    setIsDragging(true);
    setDragStart({
      x: e.clientX - screenX,
      y: e.clientY - screenY,
    });
    e.preventDefault();
    e.stopPropagation();
  }, [screenX, screenY, isResizing]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    // Don't handle drag if resizing
    if (!isDragging || isResizing || !pageRect || !pageDimensions) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    // Constrain to page bounds
    const constrainedX = Math.max(0, Math.min(newX, pageRect.width - currentWidth));
    const constrainedY = Math.max(0, Math.min(newY, pageRect.height - currentHeight));

    // Use pdf-lib dimensions if available, otherwise fallback to react-pdf dimensions
    const dimensions = pdfLibDimensions || pageDimensions;
    const ratio = pixelToPointRatio || (dimensions.width / pageRect.width);

    // Convert screen coordinates (pixels, top-left origin) to PDF points (bottom-left origin)
    // X coordinate: multiply by ratio
    const pdfX = constrainedX * ratio;
    // Y coordinate: flip from top-left to bottom-left origin
    const pdfY = dimensions.height - (constrainedY * ratio);

    // IMPORTANT: Only update position (x, y), NOT dimensions (width, height)
    // Drag should only move the field, not resize it
    onUpdate({ x: pdfX, y: pdfY });
  }, [isDragging, isResizing, dragStart, pageRect, pageDimensions, pdfLibDimensions, pixelToPointRatio, currentWidth, currentHeight, onUpdate]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleResize = useCallback((e: React.SyntheticEvent, data: { size: { width: number; height: number } }) => {
    if (!pageRect || !pageDimensions) return;
    
    setIsResizing(true);
    const { width, height } = data.size;

    // Use pdf-lib dimensions if available, otherwise fallback to react-pdf dimensions
    const dimensions = pdfLibDimensions || pageDimensions;
    const ratio = pixelToPointRatio || (dimensions.width / pageRect.width);

    if (isQRCode) {
      // For QR codes: maintain 1:1 aspect ratio
      const screenLarger = Math.max(width, height);
      setCurrentWidth(screenLarger);
      setCurrentHeight(screenLarger);

      // Convert screen dimensions to PDF points using ratio
      const pdfWidth = screenLarger * ratio;
      const pdfHeight = pdfWidth; // Maintain 1:1 aspect ratio

      // Update width and height
      debouncedSave({ width: pdfWidth, height: pdfHeight });
    } else if (isPhoto) {
      // For photos: maintain 7:9 aspect ratio (ICAO passport photo standard)
      // Calculate which dimension to use based on which changed more
      const widthChange = Math.abs(width - currentWidth);
      const heightChange = Math.abs(height - currentHeight);
      
      let finalWidth: number;
      let finalHeight: number;
      
      if (widthChange > heightChange) {
        // Width changed more - adjust height to maintain 7:9 ratio
        finalWidth = width;
        finalHeight = width / (7/9); // 7:9 ratio means width/height = 7/9, so height = width / (7/9)
      } else {
        // Height changed more - adjust width to maintain 7:9 ratio
        finalHeight = height;
        finalWidth = height * (7/9); // height * 7/9 = width
      }
      
      setCurrentWidth(finalWidth);
      setCurrentHeight(finalHeight);

      // Convert screen dimensions to PDF points using ratio
      const pdfWidth = finalWidth * ratio;
      const pdfHeight = finalHeight * ratio;

      // Update width and height
      debouncedSave({ width: pdfWidth, height: pdfHeight });
    } else {
      // For text fields: Always update width/height directly, never calculate from content
      setCurrentWidth(width);
      setCurrentHeight(height);

      // Convert screen dimensions to PDF points using ratio
      const pdfWidth = width * ratio;
      const pdfHeight = height * ratio;

      // Update width and height for text fields (content will align within these dimensions)
      debouncedSave({ width: pdfWidth, height: pdfHeight });
    }
  }, [pageRect, pageDimensions, pdfLibDimensions, pixelToPointRatio, isQRCode, debouncedSave]);

  const handleResizeStop = useCallback(() => {
    setIsResizing(false);
    
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Save immediately if there's a pending update
    if (pendingUpdateRef.current) {
      onUpdate(pendingUpdateRef.current);
      pendingUpdateRef.current = null;
    }
  }, [onUpdate]);

  const placeholderText = getPlaceholderText(mapping);
  // Convert fontSize from PDF points to screen pixels
  // fontSize in mapping is stored in PDF points, but we need pixels for display
  const pdfFontSize = mapping.fontSize || 16;
  const screenFontSize = pixelToPointRatio 
    ? pdfFontSize / pixelToPointRatio 
    : pdfFontSize; // Fallback if ratio not available
  const fontFamily = mapping.fontFamily || 'Helvetica';
  const color = mapping.color || '#000000';
  const alignment = mapping.alignment || 'left';
  const fontWeight = mapping.isBold ? 'bold' : 'normal';
  const fontStyle = mapping.isItalic ? 'italic' : 'normal';
  const textDecoration = mapping.isUnderlined ? 'underline' : 'none';
  const textTransformValue = (mapping.textTransform && mapping.textTransform !== 'none') ? mapping.textTransform : 'none';
  const opacityValue = mapping.opacity ?? 1;

  return (
    <div
      className="absolute group"
      style={{
        left: `${screenX}px`,
        top: `${screenY}px`,
        zIndex: 10,
      }}
    >
      <Resizable
        width={currentWidth}
        height={currentHeight}
        onResize={handleResize}
        onResizeStop={handleResizeStop}
        lockAspectRatio={isQRCode || isPhoto}
        minConstraints={isQRCode ? [50, 50] : isPhoto ? [50, 64] : [50, 20]} // 7:9 ratio: min height = min width * 9/7 ≈ 64
        maxConstraints={[
          isQRCode ? pageRect.width : isPhoto ? pageRect.width - screenX : pageRect.width - screenX,
          isQRCode ? pageRect.height : isPhoto ? pageRect.height - screenY : pageRect.height - screenY
        ]}
        resizeHandles={['se']}
      >
      <div
        className={`border-2 rounded ${
          isSelected
            ? 'border-[#001A55] bg-[#001A55] bg-opacity-20 ring-2 ring-[#001A55] ring-offset-1'
            : 'border-blue-500 bg-blue-100 bg-opacity-30'
        }`}
        style={{
          width: `${currentWidth}px`,  // Explicit pixel width
          height: `${currentHeight}px`,  // Explicit pixel height
          overflow: 'visible',  // Allow buttons to overflow outside
          minWidth: isQRCode ? '50px' : '50px',
          minHeight: isQRCode ? '50px' : '20px',
          cursor: isDragging ? 'grabbing' : isResizing ? 'nwse-resize' : 'grab',
          userSelect: 'none',
          position: 'relative',
          boxSizing: 'border-box',
        }}
        onMouseDown={(e) => {
          onSelect?.();
          handleMouseDown(e);
        }}
        title={getFieldTypeLabel(mapping.fieldType)}
      >
        <div
          className="absolute -top-6 left-0 flex items-center gap-1 bg-blue-500 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ cursor: 'grab' }}
        >
          <GripVertical className="w-3 h-3" />
          {getFieldTypeLabel(mapping.fieldType)}
        </div>

        <div
          className="flex items-center p-1 overflow-hidden"
          style={{
            width: '100%',
            height: '100%',
            justifyContent: alignment === 'left' ? 'flex-start' : 
                         alignment === 'center' ? 'center' : 
                         'flex-end',
            fontSize: `${screenFontSize}px`,
            fontFamily,
            fontWeight,
            fontStyle,
            textDecoration,
            textTransform: textTransformValue as any,
            opacity: opacityValue,
            color,
            textAlign: alignment as any,
            wordBreak: 'normal',
            overflowWrap: 'break-word',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {isQRCode ? (
            <div 
              className="flex items-center justify-center border-2 border-dashed border-gray-400 rounded bg-white"
              style={{
                width: '100%',
                height: '100%',
              }}
            >
              <span className="text-xs text-gray-500">QR Code</span>
            </div>
          ) : isPhoto ? (
            <div 
              className="flex items-center justify-center border-2 border-dashed border-gray-400 rounded bg-white"
              style={{
                width: '100%',
                height: '100%',
              }}
            >
              <span className="text-xs text-gray-500">Photo (7:9)</span>
            </div>
          ) : (
            <span 
              className={`${
                alignment === 'left' ? 'text-left' : 
                alignment === 'center' ? 'text-center' : 
                'text-right'
              }`}
              style={{
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
            >
              {placeholderText}
            </span>
          )}
        </div>

        {/* Action buttons - positioned on top border, half inside half outside */}
        <div 
          className="absolute flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            right: '0px',
            top: '-12px',  // Half button height (12px) above border, half below
            zIndex: 1000,  // Ensure buttons are above all elements including borders
          }}
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 bg-blue-500 text-white hover:bg-blue-600"
            style={{ zIndex: 1001 }}
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Edit2 className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 bg-red-500 text-white hover:bg-red-600"
            style={{ zIndex: 1001 }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <X className="h-3 w-3 text-white" />
          </Button>
        </div>
      </div>
      </Resizable>
    </div>
  );
};

